import {
  ArrowRight,
  BadgeCheck,
  Camera,
  CheckCircle2,
  Clock,
  GalleryHorizontalEnd,
  ImagePlus,
  LockKeyhole,
  Play,
  Sparkles,
  UploadCloud,
} from "lucide-react";
import { FormEvent, useState } from "react";
import { Link, NavLink } from "react-router-dom";

type DemoPage = "hub" | "upload" | "gallery";

const demoBasePath = "/demo/memory-media-vault";

const demoEvent = {
  clientName: "Golden Legacy Family",
  eventName: "Golden Legacy Celebration",
  eventType: "Celebration",
  memoryHubTitle: "Golden Legacy Celebration Memory Hub",
  memoryHubIntroText: "Upload, view, and revisit approved memories from this special celebration.",
  uploadPageTitle: "Share Your Golden Legacy Memories",
  uploadPageIntroText:
    "Guests can submit photos and videos through a secure upload experience. Submitted memories may be reviewed before appearing in the gallery.",
  galleryTitle: "Golden Legacy Celebration Memory Gallery",
  galleryIntroText: "A curated collection of approved memories from the Golden Legacy Celebration.",
  emptyGalleryMessage: "Approved memories will appear here once they are reviewed.",
};

const sampleGallery = [
  {
    title: "Golden Toast",
    detail: "Shared by a guest",
    type: "Photo",
    icon: Camera,
    background: "linear-gradient(135deg, #2c2114 0%, #9b7a2b 42%, #FF6A2B 100%)",
  },
  {
    title: "Family Table",
    detail: "Approved memory",
    type: "Photo",
    icon: GalleryHorizontalEnd,
    background: "linear-gradient(135deg, #101010 0%, #4f4232 44%, #FFF3EC 100%)",
  },
  {
    title: "Dance Floor Moment",
    detail: "Sample video card",
    type: "Video",
    icon: Play,
    background: "linear-gradient(135deg, #050505 0%, #7a2e2e 50%, #FF6A2B 100%)",
  },
  {
    title: "Legacy Portraits",
    detail: "Featured sample",
    type: "Photo",
    icon: Sparkles,
    background: "linear-gradient(135deg, #332411 0%, #6d5b37 48%, #fff9ef 100%)",
  },
  {
    title: "Candlelight Welcome",
    detail: "Approved memory",
    type: "Photo",
    icon: Camera,
    background: "linear-gradient(135deg, #14110b 0%, #D94A16 46%, #f8e5a2 100%)",
  },
  {
    title: "Message From Loved Ones",
    detail: "Sample video card",
    type: "Video",
    icon: Play,
    background: "linear-gradient(135deg, #060606 0%, #314236 45%, #FF6A2B 100%)",
  },
];

const reviewSteps = [
  {
    icon: UploadCloud,
    title: "Guests upload",
    copy: "Guests can submit photos and videos from a phone, tablet, or desktop browser.",
  },
  {
    icon: Clock,
    title: "Memories wait for review",
    copy: "Submitted media is reviewed before it becomes part of the public gallery.",
  },
  {
    icon: BadgeCheck,
    title: "Approved memories appear",
    copy: "Only approved photos and videos are shown in the Memory Gallery.",
  },
];

export function MemoryMediaVaultDemoPage({ page }: { page: DemoPage }) {
  return (
    <div className="demo-vault-shell min-h-screen text-white">
      <header className="sticky top-0 z-40 border-b border-ktd-gold/18 bg-ktd-black/92 px-4 py-4 backdrop-blur-xl sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Link to={demoBasePath} className="focus-ring inline-flex items-center gap-3 rounded-lg">
            <span className="grid size-11 place-items-center rounded-lg border border-ktd-gold/30 bg-white/[0.06] font-display text-lg font-black text-ktd-softGold">
              GL
            </span>
            <span className="leading-tight">
              <span className="block font-display text-lg font-bold">{demoEvent.eventName}</span>
              <span className="block text-xs font-bold uppercase tracking-[0.18em] text-ktd-softGold">Demo Memory Hub</span>
            </span>
          </Link>

          <nav className="luxury-scrollbar flex w-full gap-2 overflow-x-auto pb-1 sm:w-auto sm:pb-0" aria-label="Demo navigation">
            <DemoNavLink to={demoBasePath} end>
              Memory Hub
            </DemoNavLink>
            <DemoNavLink to={`${demoBasePath}/upload`}>Upload Preview</DemoNavLink>
            <DemoNavLink to={`${demoBasePath}/gallery`}>Gallery Preview</DemoNavLink>
          </nav>
        </div>
      </header>

      {page === "hub" && <DemoHub />}
      {page === "upload" && <DemoUpload />}
      {page === "gallery" && <DemoGallery />}

      <DemoFooter />
    </div>
  );
}

function DemoNavLink({ children, to, end = false }: { children: string; to: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `focus-ring whitespace-nowrap rounded-lg px-4 py-2 text-sm font-bold transition ${
          isActive ? "bg-ktd-gold text-ktd-black shadow-gold" : "text-white/76 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function DemoHub() {
  return (
    <main>
      <section className="px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1fr_.84fr] lg:items-center">
          <div className="demo-vault-masthead rounded-lg p-7 shadow-luxe sm:p-10">
            <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-ktd-softGold">
              {demoEvent.clientName} Sample Experience
            </p>
            <h1 className="mt-4 max-w-4xl text-balance font-display text-5xl font-bold leading-[0.98] sm:text-6xl">
              {demoEvent.memoryHubTitle}
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-white/72">{demoEvent.memoryHubIntroText}</p>
            <p className="mt-4 max-w-3xl text-base leading-7 text-white/62">
              This demo shows how a public Memory Hub can invite guests to share memories while keeping the public gallery
              curated and review-based.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to={`${demoBasePath}/upload`}
                className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg bg-ktd-gold px-5 py-3 text-sm font-extrabold text-ktd-black shadow-gold transition hover:bg-ktd-softGold"
              >
                Preview Upload <ArrowRight className="size-4" />
              </Link>
              <Link
                to={`${demoBasePath}/gallery`}
                className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg border border-ktd-gold/45 px-5 py-3 text-sm font-extrabold text-ktd-softGold transition hover:bg-white/8"
              >
                Preview Gallery <ArrowRight className="size-4" />
              </Link>
            </div>
          </div>

          <div className="grid gap-4">
            <div className="rounded-lg border border-ktd-gold/25 bg-ktd-ivory p-5 text-ktd-rich shadow-luxe">
              <div className="flex items-center justify-between gap-4">
                <span className="rounded-full bg-ktd-black px-3 py-1 text-xs font-extrabold uppercase tracking-[0.14em] text-ktd-softGold">
                  Demo Preview
                </span>
                <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#D94A16]">No real uploads</span>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {sampleGallery.slice(0, 4).map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className={`relative overflow-hidden rounded-lg border border-ktd-gold/20 ${index === 0 ? "col-span-2 aspect-[16/8]" : "aspect-square"}`}
                      style={{ background: item.background }}
                    >
                      <div className="absolute inset-0 bg-black/10" />
                      <Icon className="absolute bottom-4 left-4 size-8 text-white drop-shadow" aria-hidden="true" />
                      <p className="absolute bottom-4 right-4 rounded-full bg-black/58 px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] text-white">
                        {item.type}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
              {reviewSteps.map((step) => {
                const Icon = step.icon;
                return (
                  <article key={step.title} className="rounded-lg border border-ktd-gold/20 bg-white/[0.06] p-5">
                    <Icon className="size-6 text-ktd-softGold" aria-hidden="true" />
                    <h2 className="mt-4 font-extrabold">{step.title}</h2>
                    <p className="mt-2 text-sm leading-6 text-white/62">{step.copy}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-5 md:grid-cols-3">
          {[
            "Personalized public wording for the event.",
            "Guest-friendly upload flow with review expectations.",
            "A curated gallery that shows only approved memories.",
          ].map((copy) => (
            <div key={copy} className="rounded-lg border border-ktd-gold/20 bg-white/[0.05] p-6">
              <CheckCircle2 className="size-7 text-ktd-softGold" aria-hidden="true" />
              <p className="mt-4 text-sm font-semibold leading-7 text-white/68">{copy}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

function DemoUpload() {
  const [message, setMessage] = useState("");

  const submitPreview = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("This demo upload form is for preview purposes only. Real client upload links are provided after setup.");
  };

  return (
    <main className="px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
      <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[.82fr_1.18fr]">
        <aside className="demo-vault-masthead rounded-lg p-7 shadow-luxe sm:p-9">
          <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-ktd-softGold">Upload Preview</p>
          <h1 className="mt-4 text-balance font-display text-4xl font-bold leading-tight sm:text-5xl">
            {demoEvent.uploadPageTitle}
          </h1>
          <p className="mt-5 text-lg leading-8 text-white/72">{demoEvent.uploadPageIntroText}</p>
          <div className="mt-8 rounded-lg border border-ktd-gold/22 bg-white/[0.06] p-5">
            <LockKeyhole className="size-7 text-ktd-softGold" aria-hidden="true" />
            <h2 className="mt-4 text-xl font-extrabold">Review before public display</h2>
            <p className="mt-3 text-sm leading-7 text-white/64">
              A live client upload page sends submitted media into Pending Review. This sample page does not send files
              anywhere.
            </p>
          </div>
        </aside>

        <form onSubmit={submitPreview} className="upload-form vault-rail ivory-card rounded-lg p-5 text-ktd-rich sm:p-8">
          <div className="mb-6 rounded-lg border border-ktd-gold/24 bg-white p-4">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#D94A16]">Demo upload form</p>
            <p className="mt-2 text-sm font-semibold leading-6 text-ktd-muted">
              Preview only. This form does not submit files, create records, or connect to a private vault.
            </p>
          </div>

          <div className="grid gap-5">
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Guest name
              <input className="form-field focus-ring w-full rounded-lg px-4 py-3 font-semibold" placeholder="Your name" />
            </label>
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Email or phone
              <input className="form-field focus-ring w-full rounded-lg px-4 py-3 font-semibold" placeholder="Optional" />
            </label>
            <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
              Caption/message
              <textarea
                rows={4}
                className="form-field focus-ring w-full rounded-lg px-4 py-3 font-semibold"
                placeholder="Add a memory, note, or caption"
              />
            </label>
            <label className="grid gap-3 rounded-lg border border-dashed border-ktd-gold/45 bg-white/95 p-4 text-sm font-extrabold text-ktd-rich sm:p-5">
              <span className="flex items-center gap-3">
                <ImagePlus className="size-6 text-[#D94A16]" />
                Sample file picker
              </span>
              <input
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.webp,.mp4,.mov,.webm,image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                className="upload-file-input focus-ring w-full rounded-lg bg-white text-sm font-semibold"
              />
              <span className="text-xs font-semibold leading-5 text-[#4d4234]">
                Preview only. Accepted examples: jpg, jpeg, png, webp, mp4, mov, webm.
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-lg border border-ktd-gold/18 bg-white p-4 text-sm font-semibold leading-6 text-[#3c3328]">
              <input type="checkbox" className="mt-1 size-4 accent-ktd-gold" />
              <span>I understand that my submitted photos and videos may be reviewed before appearing in the Memory Hub gallery.</span>
            </label>
            <div className="rounded-lg border border-ktd-gold/24 bg-white p-5">
              <p className="text-sm font-extrabold text-ktd-rich">Upload verification preview</p>
              <div className="mt-4 flex min-h-24 items-center gap-4 rounded-lg border border-ktd-gold/18 bg-[#fffdf8] p-4">
                <LockKeyhole className="size-7 shrink-0 text-[#D94A16]" aria-hidden="true" />
                <p className="text-sm font-semibold leading-6 text-ktd-muted">
                  A live Memory Media Vault upload link includes verification before files are submitted.
                </p>
              </div>
            </div>
            {message && <p className="rounded-lg bg-ktd-black p-4 text-sm font-semibold leading-6 text-ktd-softGold">{message}</p>}
            <button type="submit" className="focus-ring rounded-lg bg-ktd-black px-6 py-3 text-sm font-extrabold text-ktd-softGold shadow-gold transition hover:bg-[#17120a]">
              Preview Submit for Review
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}

function DemoGallery() {
  return (
    <main className="px-4 py-12 sm:px-6 lg:px-8">
      <section className="mx-auto max-w-7xl">
        <header className="demo-vault-masthead rounded-lg p-8 shadow-luxe sm:p-10">
          <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-ktd-softGold">Gallery Preview</p>
          <h1 className="mt-4 text-balance font-display text-4xl font-bold sm:text-5xl">{demoEvent.galleryTitle}</h1>
          <p className="mt-4 max-w-3xl leading-7 text-white/68">{demoEvent.galleryIntroText}</p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to={demoBasePath}
              className="focus-ring inline-flex items-center justify-center rounded-lg border border-ktd-gold/45 px-5 py-3 text-sm font-extrabold text-ktd-softGold transition hover:bg-white/8"
            >
              Memory Hub
            </Link>
            <Link
              to={`${demoBasePath}/upload`}
              className="focus-ring inline-flex items-center justify-center rounded-lg bg-ktd-gold px-5 py-3 text-sm font-extrabold text-ktd-black shadow-gold transition hover:bg-ktd-softGold"
            >
              Upload Preview
            </Link>
          </div>
        </header>

        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {sampleGallery.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.title} className="gallery-card overflow-hidden rounded-lg border border-ktd-gold/20 bg-ktd-ivory text-ktd-rich shadow-luxe">
                <div className="relative aspect-[4/3]" style={{ background: item.background }}>
                  <div className="absolute inset-0 bg-black/10" />
                  <Icon className="absolute left-5 top-5 size-9 text-white drop-shadow" aria-hidden="true" />
                  <span className="absolute bottom-5 left-5 rounded-full bg-black/62 px-3 py-1 text-xs font-extrabold uppercase tracking-[0.14em] text-white">
                    {item.type}
                  </span>
                </div>
                <div className="border-t border-ktd-gold/15 p-5">
                  <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#D94A16]">Approved sample</p>
                  <h2 className="mt-3 text-lg font-extrabold">{item.title}</h2>
                  <p className="mt-2 text-sm text-ktd-muted">{item.detail}</p>
                </div>
              </article>
            );
          })}
        </div>

        <div className="ivory-card mt-8 rounded-lg p-6 text-ktd-rich sm:p-8">
          <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#D94A16]">Empty gallery state</p>
          <p className="mt-3 text-sm font-semibold leading-7 text-ktd-muted">{demoEvent.emptyGalleryMessage}</p>
        </div>
      </section>
    </main>
  );
}

function DemoFooter() {
  return (
    <footer className="border-t border-ktd-gold/20 px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[1fr_.9fr] lg:items-center">
        <div className="rounded-lg border border-ktd-gold/22 bg-white/[0.05] p-6">
          <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-ktd-softGold">Want this for your event?</p>
          <h2 className="mt-3 font-display text-3xl font-bold">Create a guest upload and gallery experience without exposing the private vault.</h2>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-white/64">
            Kreatethedate can personalize the public Memory Hub, upload page, and gallery around the story and style of
            your celebration, organization, fundraiser, retreat, conference, or milestone.
          </p>
          <Link
            to="/contact"
            className="focus-ring mt-6 inline-flex items-center justify-center gap-2 rounded-lg bg-ktd-gold px-5 py-3 text-sm font-extrabold text-ktd-black shadow-gold transition hover:bg-ktd-softGold"
          >
            Request Memory Media Vault <ArrowRight className="size-4" />
          </Link>
        </div>
        <div className="rounded-lg border border-ktd-gold/18 bg-ktd-black/50 p-6 text-sm leading-7 text-white/62">
          This is a sample Memory Media Vault experience created for demonstration purposes. Each client's Memory Hub,
          upload page, and gallery can be personalized with their event name, colors, wording, images, and overall style.
        </div>
      </div>
    </footer>
  );
}
